/**
 * A bookmark that exists only in this tab.
 *
 * `onDialogSave` mints a temp id for the optimistic card it shows while the
 * save is in flight. Normally the server's id replaces it moments later. Two
 * paths deliberately leave the temp id in place instead, because the save has
 * not reached the server at all:
 *
 *   - the browser was offline, so the payload went straight to the offline
 *     sync queue, and
 *   - the save died in transit and `useLinkPolling` spent its ~2 minutes
 *     without finding the link server-side, so `queueLostSave` parked the
 *     payload in that same queue (LINKFLARE-X / 140787295).
 *
 * In both cases the card the user is looking at is backed by a QUEUED PAYLOAD,
 * not by a row. That makes deleting one a different operation from deleting a
 * bookmark: there is nothing on the server to remove, and the thing that would
 * bring it back is the parked save, not a row.
 *
 * Sentry linkflare LINKFLARE-1B / 145218632 is the visible half of getting
 * that wrong — a `DELETE /links/:id` for an id the server has never issued,
 * which can only 404. The costly half is silent: the parked payload survives
 * the delete and the next flush re-creates the bookmark the user removed
 * (lessons.md: deletion needs a tombstone, never absence).
 *
 * Kept dependency-free and side-effect-free at import time so the policy is
 * unit-testable without a network or a Vue app — same shape as
 * `deleteOutcome.js`, `linkSaveOutcome.js` and `requestRetry.js`.
 */

/** Prefix `onDialogSave` mints its optimistic ids with. */
export const TEMP_BOOKMARK_ID_PREFIX = 'temp-'

/**
 * Is this card's id one we minted locally rather than one the server issued?
 *
 * Server ids are numbers (and arrive as numbers from the API), so a string
 * carrying the temp prefix is unambiguous. Written as a prefix test rather
 * than a regex on the timestamp so it keeps holding if the suffix ever
 * changes shape.
 *
 * @param {*} id — a bookmark card's id
 * @returns {boolean}
 */
export function isPendingBookmarkId(id) {
  return typeof id === 'string' && id.startsWith(TEMP_BOOKMARK_ID_PREFIX)
}

/**
 * Split a bulk selection into ids the server can act on and cards that only
 * exist locally. Bulk delete has to make the same distinction the single
 * delete does, and doing it in one place keeps the two from drifting.
 *
 * @param {Array<*>} ids
 * @returns {{persisted: Array<*>, pending: Array<*>}}
 */
export function partitionPendingIds(ids) {
  const persisted = []
  const pending = []
  for (const id of ids || []) {
    (isPendingBookmarkId(id) ? pending : persisted).push(id)
  }
  return { persisted, pending }
}

/**
 * Thrown instead of sending a request that names a pending card's temp id.
 *
 * Per-link endpoints take a numeric id, so a temp id fails in one of two ways.
 * In a path or query param Jersey cannot convert it and the API answers its
 * canned 404, which the API client reports at error level (LINKFLARE-1B,
 * LINKFLARE-1G). In a `PUT /links` body it is worse and silent:
 * `Link.tryParse` swallows the parse error, so the "update" becomes a CREATE
 * by URL, and the parked payload later replays over it and reverts the edit.
 *
 * Named so callers can tell "this card has no row yet" from a failure, and
 * thrown before any request so nothing reaches Sentry.
 */
export class PendingLinkError extends Error {
  constructor(linkId) {
    super(`Link ${linkId} has not reached the server yet`)
    this.name = 'PendingLinkError'
    this.linkId = linkId
  }
}

/**
 * @param {*} error
 * @returns {boolean} whether it is a refusal to act on a pending card. Checks
 *   the name as well as the class, since a test's module reset (or a second
 *   bundle copy) breaks `instanceof`.
 */
export function isPendingLinkError(error) {
  return error instanceof PendingLinkError || error?.name === 'PendingLinkError'
}

/**
 * The one gate every per-link API method puts its id through: returns the id
 * unchanged, or throws `PendingLinkError` for a temp id. A missing id passes,
 * because a create carries none.
 *
 * @param {*} id
 * @returns {*} the same id
 */
export function serverLinkId(id) {
  if (isPendingBookmarkId(id)) throw new PendingLinkError(id)
  return id
}

/**
 * Turn a full edit-dialog body into the changes that belong in a pending
 * card's parked CREATE payload.
 *
 * Two fields on a pending card are placeholders the client made up rather than
 * values the user chose: its title is the URL, and its type is `GENERIC`. On a
 * create the server's analyzer fills both in, so writing the placeholders into
 * the payload would override that. They go in only when the user changed them.
 * The temp id never goes in: a parked payload is a create.
 *
 * A rename made in the dialog arrives as `custom_title` and goes in as that, so
 * the replayed create stores it as the user's own title. It never goes into
 * `title`: on a create, `title` becomes the page title of a new `links` row,
 * shared with everyone who later saves the URL.
 *
 * @param {object} card — the pending card the dialog was opened on
 * @param {object} linkData — the body `onEditSave` built for `PUT /links`
 * @returns {object}
 */
export function parkedEditChanges(card, linkData) {
  const { id: _tempId, title, type, custom_title: customTitle, ...changes } = linkData || {}
  if (customTitle !== undefined) changes.custom_title = customTitle
  else if (title !== (card?.title ?? null)) changes.title = title
  if (type !== undefined && type !== (card?.type || 'GENERIC')) changes.type = type
  return changes
}
